import { useMemo, useRef } from "react";
import { wallSegments, doorArcs, roomColor, fmtFt } from "@/lib/walls";
import { FitoutItems, ElectricalLayer, PlumbingLayer } from "@/components/Fitout2D";

const S = 16;

const Dim = ({ x1, y1, x2, y2, label, vertical }) => (
  <g stroke="#94A3B8" strokeWidth="1" fill="none">
    <line x1={x1} y1={y1} x2={x2} y2={y2} />
    {vertical ? (
      <>
        <line x1={x1 - 5} y1={y1} x2={x1 + 5} y2={y1} />
        <line x1={x2 - 5} y1={y2} x2={x2 + 5} y2={y2} />
        <text x={x1 - 8} y={(y1 + y2) / 2} fill="#94A3B8" stroke="none" fontSize="11" textAnchor="middle" fontFamily="JetBrains Mono" transform={`rotate(-90 ${x1 - 8} ${(y1 + y2) / 2})`}>{label}</text>
      </>
    ) : (
      <>
        <line x1={x1 - 4} y1={y1 + 4} x2={x1 + 4} y2={y1 - 4} />
        <line x1={x2 - 4} y1={y2 + 4} x2={x2 + 4} y2={y2 - 4} />
        <text x={(x1 + x2) / 2} y={y1 - 6} fill="#94A3B8" stroke="none" fontSize="11" textAnchor="middle" fontFamily="JetBrains Mono">{label}</text>
      </>
    )}
  </g>
);

const Openings = ({ plan, bg }) => (
  <>
    {plan.windows.map((w, i) => {
      const h = w.orientation === "h";
      const [x1, y1, x2, y2] = h ? [w.x, w.y, w.x + w.width, w.y] : [w.x, w.y, w.x, w.y + w.width];
      return (
        <g key={`w${i}`}>
          <line x1={x1 * S} y1={y1 * S} x2={x2 * S} y2={y2 * S} stroke={bg} strokeWidth={8} />
          <line x1={x1 * S} y1={y1 * S} x2={x2 * S} y2={y2 * S} stroke="#00F0FF" strokeWidth={8} strokeOpacity={0.2} />
          <line x1={x1 * S} y1={y1 * S} x2={x2 * S} y2={y2 * S} stroke="#00F0FF" strokeWidth={1.5} />
        </g>
      );
    })}
    {doorArcs(plan).map((d, i) => {
      const [hx, hy] = d.hinge, [cx, cy] = d.closed, [ox, oy] = d.open;
      const cross = (cx - hx) * (oy - hy) - (cy - hy) * (ox - hx);
      return (
        <g key={`d${i}`}>
          <line x1={hx * S} y1={hy * S} x2={cx * S} y2={cy * S} stroke={bg} strokeWidth={9} />
          <line x1={hx * S} y1={hy * S} x2={ox * S} y2={oy * S} stroke="#FFB800" strokeWidth={2} />
          <path d={`M ${cx * S} ${cy * S} A ${d.w * S} ${d.w * S} 0 0 ${cross > 0 ? 1 : 0} ${ox * S} ${oy * S}`} stroke="#FFB800" strokeWidth={1} strokeDasharray="3 3" fill="none" />
        </g>
      );
    })}
  </>
);

const Stair = ({ r, dir }) => {
  const alongX = r.w >= r.h, n = Math.floor(alongX ? r.w : r.h);
  const lines = Array.from({ length: Math.max(0, n - 1) }, (_, k) => k + 1);
  const [cy, cx] = [(r.y + r.h / 2) * S, (r.x + r.w / 2) * S];
  return (
    <g pointerEvents="none" stroke="#C084FC" strokeOpacity={0.55} strokeWidth={1}>
      {lines.map((k) => alongX
        ? <line key={k} x1={(r.x + k) * S} y1={r.y * S} x2={(r.x + k) * S} y2={(r.y + r.h) * S} />
        : <line key={k} x1={r.x * S} y1={(r.y + k) * S} x2={(r.x + r.w) * S} y2={(r.y + k) * S} />)}
      {dir && (alongX
        ? <line x1={(r.x + 0.8) * S} y1={cy + 18} x2={(r.x + r.w - 0.8) * S} y2={cy + 18} strokeOpacity={1} strokeWidth={1.5} markerEnd="url(#arrow)" />
        : <line x1={cx + 18} y1={(r.y + 0.8) * S} x2={cx + 18} y2={(r.y + r.h - 0.8) * S} strokeOpacity={1} strokeWidth={1.5} markerEnd="url(#arrow)" />)}
    </g>
  );
};

const useWallDrag = (onWallDrag) => {
  const svgRef = useRef(null);
  const drag = useRef(null);
  const toFt = (e) => {
    const pt = svgRef.current.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const p = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
    return [p.x / S, p.y / S];
  };
  const down = (seg) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    svgRef.current.setPointerCapture(e.pointerId);
    drag.current = { seg, start: toFt(e), d: 0 };
  };
  const move = (e) => {
    if (!drag.current) return;
    const p = toFt(e), { seg, start } = drag.current;
    const d = Math.round((seg.o === "v" ? p[0] - start[0] : p[1] - start[1]) * 2) / 2;
    if (d !== drag.current.d) { drag.current.d = d; onWallDrag(seg, d, false); }
  };
  const up = () => {
    if (!drag.current) return;
    const { seg, d } = drag.current;
    drag.current = null;
    onWallDrag(seg, d, true);
  };
  return { svgRef, down, move, up };
};

export const FloorPlan2D = ({ plan, selectedId, onSelect, compact = false, bg = "#0A0D14", animate = false, onWallDrag, stairDir, layers, hideDims = false }) => {
  const segs = useMemo(() => wallSegments(plan), [plan]);
  const { svgRef, down, move, up } = useWallDrag(onWallDrag);
  const pad = compact ? 12 : 64;
  const W = plan.width * S, D = plan.depth * S;
  const topRooms = plan.rooms.filter((r) => r.y === 0).sort((a, b) => a.x - b.x);

  return (
    <svg ref={svgRef} viewBox={`${-pad} ${-pad} ${W + pad * 2} ${D + pad * 2}`} className="w-full h-full" data-testid={compact ? undefined : "floorplan-svg"} preserveAspectRatio="xMidYMid meet"
      onPointerMove={onWallDrag ? move : undefined} onPointerUp={onWallDrag ? up : undefined} onPointerCancel={onWallDrag ? up : undefined}
      style={onWallDrag ? { touchAction: "none", userSelect: "none", WebkitUserSelect: "none" } : undefined}>
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" fill="#C084FC" />
        </marker>
      </defs>
      {plan.rooms.map((r) => {
        const sel = r.id === selectedId;
        const c = roomColor(r.type);
        return (
          <g key={r.id} onClick={() => onSelect?.(r.id)} className={onSelect ? "cursor-pointer" : ""} data-testid={compact ? undefined : `room-${r.id}`}>
            <rect x={r.x * S} y={r.y * S} width={r.w * S} height={r.h * S} fill={c} fillOpacity={sel ? 0.28 : 0.07} className="transition-[fill-opacity] duration-300 hover:fill-opacity-20" />
            {r.type === "stair" && <Stair r={r} dir={compact ? null : stairDir} />}
            {!compact && (
              <text x={(r.x + r.w / 2) * S} y={(r.y + r.h / 2) * S} textAnchor="middle" fill="#F1F5F9" fontFamily="Archivo" pointerEvents="none">
                <tspan x={(r.x + r.w / 2) * S} dy="-0.4em" fontSize={Math.max(6, Math.min(12, (r.w * S * 0.88) / (r.name.length * 0.68)))} fontWeight="600">{r.name.toUpperCase()}</tspan>
                {!hideDims && <tspan x={(r.x + r.w / 2) * S} dy="1.4em" fontSize="10" fill="#94A3B8" fontFamily="JetBrains Mono">{fmtFt(r.w)} × {fmtFt(r.h)}</tspan>}
                {!hideDims && <tspan x={(r.x + r.w / 2) * S} dy="1.3em" fontSize="10" fill={c} fontFamily="JetBrains Mono">{Math.round(r.area)} SF{r.type === "stair" && stairDir ? ` · ${stairDir}` : ""}</tspan>}
              </text>
            )}
          </g>
        );
      })}
      <g className={animate ? "draw-in" : ""}>
        {layers?.furniture && plan.fitout && <FitoutItems items={plan.fitout.items} />}
        {segs.map((s, i) => (
          <line key={i} x1={(s.o === "h" ? s.s : s.at) * S} y1={(s.o === "h" ? s.at : s.s) * S}
            x2={(s.o === "h" ? s.e : s.at) * S} y2={(s.o === "h" ? s.at : s.e) * S}
            stroke="#E2E8F0" strokeWidth={s.ext ? 7 : 4} strokeLinecap="square" />
        ))}
      </g>
      <Openings plan={plan} bg={bg} />
      {layers?.plumbing && plan.fitout && <PlumbingLayer items={plan.fitout.plumbing} />}
      {layers?.electrical && plan.fitout && <ElectricalLayer items={plan.fitout.electrical} />}
      {onWallDrag && segs.map((s, i) => !s.ext && (
        <line key={`h${i}`} data-testid={`wall-handle-${i}`} onPointerDown={down(s)}
          x1={(s.o === "h" ? s.s : s.at) * S} y1={(s.o === "h" ? s.at : s.s) * S}
          x2={(s.o === "h" ? s.e : s.at) * S} y2={(s.o === "h" ? s.at : s.e) * S}
          strokeWidth={14} className="stroke-transparent hover:stroke-cyan-400/40 transition-colors"
          style={{ cursor: s.o === "v" ? "col-resize" : "row-resize" }} />
      ))}
      {!compact && (
        <>
          {!hideDims && <>
            <Dim x1={0} y1={-44} x2={W} y2={-44} label={fmtFt(plan.width)} />
            {topRooms.map((r) => <Dim key={r.id} x1={r.x * S} y1={-20} x2={(r.x + r.w) * S} y2={-20} label={fmtFt(r.w)} />)}
            <Dim x1={-28} y1={0} x2={-28} y2={D} label={fmtFt(plan.depth)} vertical />
          </>}
          {hideDims && <text x={W / 2} y={-24} textAnchor="middle" fontFamily="JetBrains Mono" fontSize="11" fill="#FFB800" data-testid="dims-locked-label">DIMENSIONS UNLOCK AFTER PAYMENT</text>}
          <g transform={`translate(${W + 30}, ${D - 10})`} fontFamily="JetBrains Mono" fontSize="10" fill="#94A3B8">
            <path d="M0 -30 L6 -14 L0 -18 L-6 -14 Z" fill="#00F0FF" />
            <text y="0" textAnchor="middle">N</text>
          </g>
        </>
      )}
    </svg>
  );
};
